/**
 * The single source of truth for every route the SPA answers on.
 *
 * Three consumers read this file, and they ask two different questions:
 *
 *   1. "Does this route exist?" — membership in ROUTES.
 *      The CloudFront viewer-request function uses it to return 404 for
 *      invented URLs while still serving the SPA shell for real deep links.
 *      Without it every path returns 200 and the app becomes a crawl trap.
 *
 *   2. "May a search engine index this route?" — the `indexable` flag.
 *      robots.txt and the per-page `noindex` meta tag read it.
 *
 * An indexable route can also carry a `description`. RouteMeta writes it to
 * the description meta tag. Without one, the default in index.html stays.
 *
 * `indexable` is opt-in. A new route is not indexable until someone says so,
 * because that is the safe direction to fail.
 *
 * CONTRACT: this file is plain data and imports nothing. The `seoArtifacts`
 * plugin in vite.config.ts imports it in Node at build time, so it must never
 * pull in React or any browser API.
 *
 * `indexable` is NOT "does this route need a session". Auth lives in
 * ProtectedRoute / GuestRoute / AdminRoute and is not duplicated here. Some
 * routes below need no session and still must stay out of the index, because
 * they are one-shot token links: /reset-password, /verify-email,
 * /email-change/*, /account/delete/confirm, /oauth/consent.
 *
 * Paths use the React Router param syntax (`:id`). appRoutes.tsx builds the
 * real router from these paths, and routes/__tests__/manifest.test.ts fails the
 * build if the two ever disagree.
 */

/** The one hostname the app answers on. Must match `custom.publicHost` in serverless.yml. */
export const CANONICAL_HOST = 'app.netfiscus.com';

export interface RouteDefinition {
    /** Full path from the root, React Router syntax. */
    path: string;
    /** Opt in to search engine indexing. Absent means no. */
    indexable?: true;
    /** Meta description for the search result snippet. Absent keeps the index.html default. */
    description?: string;
}

export const ROUTES: readonly RouteDefinition[] = [
    // Indexable. These four serve brand navigational queries such as
    // "netfiscus login", so they stay in the index on purpose.
    {
        path: '/',
        indexable: true,
        description:
            'Netfiscus is a personal net worth tracker. Record your accounts, assets and debts in one place and see how your net worth changes over time.',
    },
    {
        path: '/login',
        indexable: true,
        description:
            'Sign in to Netfiscus to see your accounts, assets and debts, and track how your net worth has changed.',
    },
    {
        path: '/register',
        indexable: true,
        description:
            'Create a free Netfiscus account to track your accounts, assets and debts, and watch your net worth change over time.',
    },
    {
        path: '/forgot-password',
        indexable: true,
        description: 'Reset the password for your Netfiscus account. We send a reset link to your email address.',
    },

    // Token links. No session needed, never indexable.
    { path: '/reset-password' },
    { path: '/verify-email' },
    { path: '/oauth/consent' },
    { path: '/email-change/confirm' },
    { path: '/email-change/verify' },
    { path: '/account/delete/confirm' },

    // Authenticated application routes.
    { path: '/onboarding' },
    { path: '/dashboard' },
    { path: '/assets' },
    { path: '/debts' },
    { path: '/ledgers/:id' },
    { path: '/ledgers/:id/values' },
    { path: '/ledgers/:id/cash-flow' },
    { path: '/ledgers/:id/analytics' },
    { path: '/credits' },
    { path: '/credits/history' },
    { path: '/data' },
    { path: '/data/purge' },
    { path: '/export' },
    { path: '/feedback' },
    { path: '/profile' },
    { path: '/settings' },
    { path: '/settings/tags' },
    { path: '/settings/collections' },
    { path: '/settings/attribution' },
    { path: '/settings/preferences' },
    { path: '/settings/notifications' },
    { path: '/settings/connected-apps' },

    // Admin routes. Gated by AdminRoute on top of the session.
    { path: '/admin' },
    { path: '/admin/users' },
    { path: '/admin/transactions' },
    { path: '/admin/purchases' },
    { path: '/admin/feedback' },
    { path: '/admin/dev/circuit-breaker' },
    { path: '/admin/dev/exchange-rates' },
];

/** Paths a search engine may crawl and index. */
export const INDEXABLE_PATHS: readonly string[] = ROUTES.filter((route) => route.indexable).map(
    (route) => route.path
);

/**
 * The one spelling of a path: no trailing slash, except for the root.
 *
 * `/login` and `/login/` are the same route. Everything that compares a path to
 * the manifest, or builds a URL from it, must use this form. Otherwise the
 * canonical tag on /login/ would point at /login/ and declare a second copy of
 * the page.
 */
export function normalizePath(path: string): string {
    return path.length > 1 ? path.replace(/\/$/, '') : path;
}

/**
 * True when a search engine may index `path`.
 *
 * Exact match only. Every indexable route is static, so a param never reaches
 * here, and an unknown path is not indexable by definition.
 */
export function isIndexableRoute(path: string): boolean {
    return INDEXABLE_PATHS.includes(normalizePath(path));
}

/**
 * The meta description declared for `path`, or null to keep the default.
 *
 * Only indexable routes carry one, so a param path never matches here.
 */
export function routeDescription(path: string): string | null {
    const normalized = normalizePath(path);

    return ROUTES.find((route) => route.path === normalized)?.description ?? null;
}
